import { ArrowRightIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import {
    LessonResponse,
    LessonStatusResponse,
    YoutubeCaptionResponse,
    YoutubeVideoResponse,
} from '../../../../server/response/listeningResponseScheme';
import { apiClient } from '../../../clientUtils/apiClient';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';
import modalStore from '../../../stores/modalStore';
import { useCommonNavigate } from '../../../views/common/CommonLink';
import ThinkingText from '../../../views/common/ThinkingText';

interface CreateLessonModalContentProps {
    modalId: string;
    videoId: string;
}

type ModalState =
    | 'fetching_video' // 비디오 조회 중
    | 'video_not_found' // 비디오 없음
    | 'fetching_caption' // 자막 조회 중
    | 'caption_not_found' // 자막 없음
    | 'creating_lesson_error' // 레슨 생성 중 에러
    | 'ready' // 레슨 생성 가능
    | 'error'; // 에러 발생

class ViewStore {
    state: ModalState = 'fetching_video';
    videoResponse: YoutubeVideoResponse | null = null;
    captionResponse: YoutubeCaptionResponse | null = null;
    errorMessage: string | null = null;

    isCreatingLesson = false;

    constructor(private videoId: string) {
        makeAutoObservable(this);
    }

    async fetchVideo() {
        this.state = 'fetching_video';
        this.errorMessage = null;

        try {
            const response = await apiClient.youtube.member.ensureVideo.query({
                videoId: this.videoId,
            });

            runInAction(() => {
                this.videoResponse = response;
                this.state = 'fetching_caption';
            });

            if (response.hasEnCaptionTrack) {
                // 비디오 찾으면 자막 조회
                this.fetchCaption();
            } else {
                runInAction(() => {
                    this.state = 'caption_not_found';
                    this.errorMessage =
                        '레슨에는 영어 자막이 필요합니다. 다른 영상을 선택해주세요.';
                });
            }
        } catch (error: any) {
            runInAction(() => {
                if (error.data?.code === 'NOT_FOUND') {
                    this.state = 'video_not_found';
                    this.errorMessage = '유튜브 영상을 찾을 수 없습니다.';
                } else {
                    this.state = 'error';
                    this.errorMessage = '영상을 불러오는 중 오류가 발생했습니다.';
                }
            });
        }
    }

    async fetchCaption() {
        this.state = 'fetching_caption';

        try {
            const response = await apiClient.youtube.member.ensureCaption.query({
                videoId: this.videoId,
            });

            runInAction(() => {
                this.captionResponse = response;
                if (response.hasCaptionSegments) {
                    this.state = 'ready';
                } else {
                    this.state = 'caption_not_found';
                    this.errorMessage =
                        '영어 자막을 찾았지만 자막이 비어있습니다. 다른 영상을 선택해주세요.';
                }
            });
        } catch (error: any) {
            runInAction(() => {
                this.state = 'caption_not_found';
                this.errorMessage =
                    '영어 자막을 가져오는 중 문제가 발생했습니다. 다시 시도해주세요.';
            });
        }
    }

    get isLoading(): boolean {
        return this.state === 'fetching_video' || this.state === 'fetching_caption';
    }

    get canCreateLesson(): boolean {
        return this.state === 'ready';
    }

    get isError(): boolean {
        return (
            this.state === 'video_not_found' ||
            this.state === 'caption_not_found' ||
            this.state === 'error' ||
            this.state === 'creating_lesson_error'
        );
    }

    get statusMessage(): string {
        switch (this.state) {
            case 'fetching_video':
                return '비디오를 불러오는 중...';
            case 'fetching_caption':
                return '자막을 확인하는 중...';
            default:
                return '';
        }
    }

    async createUserLesson(): Promise<LessonStatusResponse | null> {
        if (this.isCreatingLesson) {
            return null;
        }

        this.isCreatingLesson = true;

        try {
            const lessonStatus = await apiClient.userLesson.member.createUserLesson.mutate({
                videoId: this.videoId,
            });

            return lessonStatus;
        } catch (error: any) {
            runInAction(() => {
                this.state = 'creating_lesson_error';
                this.errorMessage = '레슨 생성 중 오류가 발생했습니다. 다시 시도해주세요.';
            });
            return null;
        } finally {
            this.isCreatingLesson = false;
        }
    }
}

const CreateLessonModalContent = observer(({ modalId, videoId }: CreateLessonModalContentProps) => {
    const viewStore = useMobxInstance(ViewStore, videoId);
    const { navigate } = useCommonNavigate();

    useEffect(() => {
        viewStore.fetchVideo();
    }, [viewStore]);

    return (
        <div className="flex flex-col gap-4">
            {/* 썸네일 영역 */}
            <div className="bg-base-200 aspect-video w-full overflow-hidden rounded-lg">
                {viewStore.videoResponse?.thumbnail ? (
                    <img
                        src={viewStore.videoResponse.thumbnail}
                        alt={viewStore.videoResponse.title}
                        className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center">
                        <div className="skeleton h-full w-full" />
                    </div>
                )}
            </div>

            {/* 제목 영역 */}
            <div>
                {viewStore.videoResponse ? (
                    <>
                        <h3 className="text-base-content line-clamp-2 min-h-6 font-medium">
                            {viewStore.videoResponse.title}
                        </h3>
                        <p className="text-base-content/60 mt-1 min-h-4 text-sm">
                            {viewStore.videoResponse.channelName}
                        </p>
                    </>
                ) : (
                    <>
                        <div className="skeleton h-6 w-full" />
                        <div className="skeleton mt-1 h-4 w-1/2" />
                    </>
                )}
            </div>

            {viewStore.canCreateLesson && (
                <button
                    className={twMerge('btn btn-primary btn-block h-10')}
                    onClick={async () => {
                        if (!viewStore.canCreateLesson || viewStore.isCreatingLesson) {
                            return;
                        }

                        const lessonStatus = await viewStore.createUserLesson();
                        if (lessonStatus) {
                            navigate(`/my/lesson/${lessonStatus.hash}`);

                            // 모달 닫기
                            modalStore.close(modalId);
                        }
                    }}
                >
                    시작하기
                    <ArrowRightIcon className="size-4" />
                </button>
            )}
            {viewStore.isError && (
                <>
                    <div className={twMerge('text-error py-2 text-sm')}>
                        {viewStore.errorMessage}
                    </div>
                    <button
                        className={twMerge('btn btn-outline btn-block h-10')}
                        onClick={() => modalStore.close(modalId)}
                    >
                        닫기
                    </button>
                </>
            )}
            {viewStore.isLoading && (
                <div className="bg-base-100 flex h-10 items-center">
                    <ThinkingText color="var(--color-primary)">
                        {viewStore.statusMessage}
                    </ThinkingText>
                </div>
            )}
        </div>
    );
});

export default CreateLessonModalContent;
